import { createAsyncThunk } from "@reduxjs/toolkit"
import {
  BASE_URL,
  getBaseEndpoint,
} from "../../../features/endpoints/endpointManager"
import axios from "axios"
import { initialAuthAndReleases, practiceSlice } from "../model"
import { fileDetails, fetchB64DocxFile } from "../../CMS/fileService"
import { practiceLoaderSlice } from "./loader"
import { RootState } from "../../../reduxStore/store"
import { AuthAndReleases, Practice } from "../schema"
import { cloneDeep } from "lodash"
import { DataStatus } from "../../enums"
import {
  DOCUSIGN_REDIRECT_QUERY_PARAM_KEY,
  DocusignRedirectOrigin,
} from "../../../features/shared/hooks/useDocusignRedirect"
import { appRoutes, getRouteWithQueryParams } from "../../../routes"
import { getCurrentFormattedDateForESign } from "../helpers"

export const signedAuthAndReleaseFileBlob = "signedAuthAndReleaseFileBlob"

const getAuthAndReleasesEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/authrelease`
}
const putAuthAndReleasesRequiredEndpoint = (practiceId: string): string => {
  return (
    getBaseEndpoint() + `/practice/practices/${practiceId}/authrelease/required`
  )
}
const documentSigningRequestEndpoint = (practiceId: string): string => {
  return (
    getBaseEndpoint() +
    `/practice/practices/${practiceId}/authrelease/signingrequest`
  )
}

export const getAuthAndReleasesData = createAsyncThunk(
  "practiceProfile/authAndReleases/getAuthAndReleasesData",
  async (practiceId: string, { dispatch, getState }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        authAndReleasesDataStatus: DataStatus.SENDING,
      }),
    )

    const state = getState() as RootState

    let payload: AuthAndReleases = {
      signedFileId: null,
      signedOn: null,
      signedBy: null,
      signingUrl: "",
    }

    // get auth and releases data
    try {
      const url: string = getAuthAndReleasesEndpoint(practiceId)
      const response = await axios.get(url)

      if (response.status === 200) {
        const data = response.data

        payload = {
          signedFileId: data.executedFileId,
          signedOn: data.lastUpdatedDateTime,
          signedBy: data.signedBy,
          signingUrl: "",
        }
      }
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          authAndReleasesDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }

    const updatedPractice: Practice = cloneDeep({
      ...state.practice.practice,
      authAndReleases: { ...payload },
    })
    dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        authAndReleasesDataStatus: DataStatus.LOADED,
      }),
    )
  },
)

interface AuthAndReleasesRequiredThunkArgs {
  authAndReleasesRequired: boolean
  practiceId: string
  setAuthAndReleasesRequiredDataStatus?(status: DataStatus): void
}
export const upsertAuthAndReleasesRequired = createAsyncThunk<
  void,
  AuthAndReleasesRequiredThunkArgs
>(
  "authAndReleases/documentSigningRequest",
  async (
    {
      authAndReleasesRequired,
      practiceId,
      setAuthAndReleasesRequiredDataStatus,
    },
    { dispatch, getState },
  ) => {
    try {
      setAuthAndReleasesRequiredDataStatus?.(DataStatus.SENDING)
      const url: string = putAuthAndReleasesRequiredEndpoint(practiceId)
      const response = await axios.put(url, authAndReleasesRequired, {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      })

      if (response.status === 204) {
        const state = getState() as RootState
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          authReleaseRequired: authAndReleasesRequired,
        })
        if (!authAndReleasesRequired) {
          updatedPractice.authAndReleases = { ...initialAuthAndReleases }
        }
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        setAuthAndReleasesRequiredDataStatus?.(DataStatus.LOADED)
      }
    } catch (error) {
      setAuthAndReleasesRequiredDataStatus?.(DataStatus.ERROR)
      console.error(error)
    }
  },
)

interface DocumentSigningRequestThunkArgs {
  practiceId: string
  userName: string
  email: string
  clientId: string
}
export const documentSigningRequest = createAsyncThunk(
  "practiceProfile/authAndReleases/fetchAndEncodeDocusignTemplate",
  async (
    { practiceId, userName, email, clientId }: DocumentSigningRequestThunkArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState

    const documentBase64 = await fetchB64DocxFile(
      fileDetails.authAndRelease.staticUrl,
    )

    const redirectUrl = getRouteWithQueryParams(
      BASE_URL.concat(appRoutes.practice.LIABILITY_ROUTE),
      {
        practiceId,
        [DOCUSIGN_REDIRECT_QUERY_PARAM_KEY]:
          DocusignRedirectOrigin.AUTH_AND_RELEASES,
      },
    )

    const payload = {
      currentDate: getCurrentFormattedDateForESign(),
      documentBase64,
      documentName: "AuthorizationsAndReleasesDocument",
      documentType: "DOCX",
      documentId: crypto.randomUUID(),
      signerName: userName,
      signerEmail: email,
      signerClientId: clientId,
      redirectUrl,
    }
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        authAndReleasesSigningRequestDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      const url: string = documentSigningRequestEndpoint(practiceId)
      const response = await axios.post(url, payload)

      if (response.status === 200) {
        const data = response.data
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          authAndReleases: {
            ...state.practice.practice.authAndReleases,
            signingUrl: data.embeddedSigningURL,
          },
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      }

      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          authAndReleasesSigningRequestDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          authAndReleasesSigningRequestDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)
